import { usePage } from '@inertiajs/vue3'
import type { Router } from '@inertiajs/core'
import type { SharedProps } from '../types'
import { useFacebookCookies } from './useFacebookCookies'

/**
 * Reports page views to Google Tag Manager, and refreshes the outbound-link
 * tracking, on every navigation.
 *
 * This lives in its own module rather than in resources/js/app.js on purpose.
 * The build rewrites that entry file without producing a source map, so any
 * code left in it shows up in error reports with useless line numbers. Code
 * imported from here keeps its real positions.
 */
export function usePageViewTracking() {
    const { appendFacebookCookiesToLinks } = useFacebookCookies()

    /**
     * Reading the current page works here even though this is not inside a
     * component, because Inertia keeps the page in a module-level store rather
     * than handing it down through the component tree.
     *
     * Its contents are only ever read inside the callbacks below, though. This
     * runs before the application has started, so at this point there is no
     * page yet — by the time either callback fires, there is.
     */
    const page = usePage<SharedProps>()

    let currentPageTitle = ''

    /**
     * Work out the browser tab's title for a page, and remember it.
     *
     * Remembering it is the point. The page view reported below wants the
     * title, but by the time a navigation is announced the document's title has
     * not caught up yet — Inertia updates it a moment later. This function does
     * run at the right moment, so the title is captured here instead.
     */
    function resolvePageTitle(title: string): string {
        const { siteName } = page.props

        currentPageTitle = title ? `${title} | ${siteName}` : siteName

        return currentPageTitle
    }

    function trackPageViews(router: Router): void {
        let initialPageLoad = true

        router.on('navigate', (event) => {
            // Re-attach the ad identifiers to the outbound links. Needed on
            // every navigation, not just the first: moving between pages here
            // replaces the page's links without reloading the browser.
            appendFacebookCookiesToLinks()

            // Google Tag Manager notices the first page load by itself, but
            // not the navigations after it, which never reload the browser. So
            // each of those is reported by hand — and the first one skipped,
            // since reporting it too would count it twice.
            if (initialPageLoad) {
                initialPageLoad = false

                return
            }

            setTimeout(() => {
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'virtualPageView',
                    pageUrl: event.detail.page.url,
                    pageLocation: window.location.href,
                    pageTitle: currentPageTitle || page.props.siteName,
                })
            })
        })
    }

    return {
        resolvePageTitle,
        trackPageViews,
    }
}
